import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import * as XLSX from "xlsx";
import {
  Button,
  Container,
  Group,
  Paper,
  Text,
  Stack,
  FileButton,
  Select,
  Divider,
  SimpleGrid,
  Box,
  MultiSelect,
  rem,
  ScrollArea,
  ThemeIcon,
  Tooltip,
  Menu,
  Title,
} from "@mantine/core";
import {
  IconUpload,
  IconDownload,
  IconAlertCircle,
  IconCheck,
  IconPackage,
  IconArrowLeft,
  IconClock,
  IconAlertTriangle,
  IconFileText,
  IconUsers,
  IconChevronDown,
  IconInfoCircle,
} from "@tabler/icons-react";
import { toast } from "react-toastify";
import {
  collection,
  getDocs,
  getDoc,
  query,
  where,
  writeBatch,
  doc,
  serverTimestamp,
  increment,
  collectionGroup,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../hooks/hooks";
import { db, auth } from "../firebase/FirebaseCredential";
import BenchLoader from "../Components/UIComponent/BenchLoader/BenchLoader";
import { RootState } from "../store/store";
import ReusableSpreadsheet, {
  SpreadsheetCell,
  ColumnConfig,
  ValidationStats,
} from "../Components/ImportComponent/ReusableSpreadsheet";
import BenchSettingsBreadcrumbs from "../Components/UIComponent/Components/BenchSettingsBreadcrumbs";
import BenchTopHeader from "../Components/UIComponent/Components/BenchTopHeader";
import { useUserContext } from "../contextapi/UserContext";

type Matrix<T> = T[][];

const MIN_ROWS = 10;
const MAX_ROWS = 500;

const PRODUCT_UNITS = [
  { value: "kg", label: "kg" },
  { value: "gram", label: "gram" },
  { value: "pcs", label: "pcs" },
  { value: "box", label: "box" },
  { value: "ml", label: "ml" },
  { value: "liter", label: "liter" },
  { value: "pack", label: "pack" },
  { value: "bottle", label: "bottle" },
  { value: "can", label: "can" },
  { value: "bag", label: "bag" },
  { value: "dozen", label: "dozen" },
];

const HARDCODED_CATEGORIES = [
  { value: "Supplements", label: "Supplements" },
  { value: "Merchandise", label: "Merchandise" },
  { value: "Equipment", label: "Equipment" },
  { value: "Snacks", label: "Snacks" },
  { value: "Apparel", label: "Apparel" },
];

export default function ProductImportPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<Matrix<SpreadsheetCell>>([]);
  const [branchId, setBranchId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState<{ value: string; label: string; id?: string }[]>(HARDCODED_CATEGORIES);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const resetRef = useRef<() => void>(null);
  const [validationStats, setValidationStats] = useState<ValidationStats>({
    totalRows: 0,
    validRows: 0,
    invalidRows: 0,
    errors: [],
    errorsByColumn: {},
    requiredFieldsEmpty: 0,
  });

  const companyId = useAppSelector(
    (state: RootState) => state.session?.company_id,
  );
  const { branchesList } = useAppSelector(
    (state: RootState) => state.branchList,
  );
  const { profileDetails } = useUserContext();

  const COMMON_COLUMNS: ColumnConfig[] = useMemo(
    () => [
      {
        key: "Product Name",
        required: true,
        type: "text",
        placeholder: "Enter product name",
        width: 200,
      },
      {
        key: "Barcode",
        required: false,
        type: "text",
        placeholder: "Enter barcode (optional)",
        width: 150,
      },
      {
        key: "Category",
        required: true,
        type: "select",
        options: categories.map((c) => ({ label: c.label, value: c.value })),
        placeholder: "Select category",
        width: 180,
      },
      {
        key: "Selling Price",
        required: true,
        type: "number",
        placeholder: "0.00",
        width: 120,
        validator: (val: any) => !isNaN(Number(val)) && Number(val) >= 0,
      },
      {
        key: "Purchase Price",
        required: false,
        type: "number",
        placeholder: "0.00",
        width: 120,
        validator: (val: any) =>
          !val || (!isNaN(Number(val)) && Number(val) >= 0),
      },
      {
        key: "Unit",
        required: false,
        type: "select",
        options: PRODUCT_UNITS,
        placeholder: "Select unit",
        width: 100,
      },
      {
        key: "Unit Value",
        required: true,
        type: "number",
        placeholder: "1",
        width: 100,
        validator: (val: any) => !isNaN(Number(val)) && Number(val) >= 0,
      },
      {
        key: "Status",
        required: true,
        type: "select",
        options: ["Active", "Inactive"],
        placeholder: "Select status",
        width: 120,
      },
      {
        key: "HSN Code",
        required: false,
        type: "text",
        placeholder: "Enter HSN code",
        width: 120,
      },
      {
        key: "Tax Type",
        required: true,
        type: "select",
        options: ["Taxable", "Non Taxable", "No GST"],
        placeholder: "Select tax type",
        width: 120,
      },
      {
        key: "Tax Slab",
        required: false,
        type: "select",
        options: ["5", "12", "18"],
        placeholder: "Select tax slab",
        width: 100,
        validator: (val, row) => {
          if (!row) return true;
          const taxTypeColIndex = 10; // 'Tax Type' is at index 10 in PRODUCT_IMPORT_COLUMNS (SKU Code is index 0)
          const taxType = row[taxTypeColIndex]?.value;
          if (taxType === "Taxable" && !val) {
            return "Tax Slab is required for Taxable items";
          }
          return true;
        },
      },
      {
        key: "Preferred Vendor",
        required: false,
        type: "select",
        options: suppliers.map((s) => ({ label: s.label, value: s.label })),
        placeholder: "Select vendor",
        width: 180,
      },
      {
        key: "Description",
        required: false,
        type: "text",
        placeholder: "Enter description",
        width: 250,
      },
      {
        key: "Expiry Date",
        required: false,
        type: "date",
        placeholder: "Select expiry date",
        width: 150,
      },
      {
        key: "Manufacturing Date",
        required: false,
        type: "date",
        placeholder: "Select mfg date",
        width: 150,
      },
    ],
    [categories, suppliers],
  );

  const PRODUCT_IMPORT_COLUMNS: ColumnConfig[] = useMemo(
    () => [
      {
        key: "SKU Code",
        required: false,
        type: "text",
        placeholder: "Enter SKU Code",
        width: 150,
      },
      ...COMMON_COLUMNS,
    ],
    [COMMON_COLUMNS],
  );

  const currentColumnConfig = PRODUCT_IMPORT_COLUMNS;

  const fetchDependencies = async () => {
    if (!companyId) return;
    try {
      // Fetch Categories
      const catSnap = await getDocs(
        collection(db, "companyprofile", companyId, "productcategory"),
      );
      const customCatList = catSnap.docs
        .map((doc) => {
          const name = doc.data().name;
          return name ? { label: name, value: name, id: doc.id } : null;
        })
        .filter(Boolean) as { label: string; value: string; id: string }[];

      const categoryMap = new Map<string, { label: string; value: string; id?: string }>();
      HARDCODED_CATEGORIES.forEach((c) => categoryMap.set(c.value.toLowerCase(), c));
      customCatList.forEach((c) => categoryMap.set(c.value.toLowerCase(), c));

      setCategories(Array.from(categoryMap.values()));

      // Fetch Suppliers (Vendors)
      const vendorSnap = await getDocs(
        query(
          collectionGroup(db, "vendoraccounts"),
          where("company_id", "==", companyId),
        ),
      );
      const vendorList = vendorSnap.docs.map((doc) => ({
        label:
          doc.data().companyName ||
          `${doc.data().firstName} ${doc.data().lastName}`,
        value:
          doc.data().companyName ||
          `${doc.data().firstName} ${doc.data().lastName}`,
        id: doc.id,
      }));
      setSuppliers(vendorList);
    } catch (error) {
      console.error("Error fetching dependencies:", error);
    }
  };

  useEffect(() => {
    fetchDependencies();
  }, [companyId]);

  useEffect(() => {
    if (branchesList && branchesList.length > 0) {
      const validBranches = branchesList.filter(
        (b: any) => b.id !== "all_branches",
      );
      if (validBranches.length > 0) {
        setBranchId(validBranches[0].id);
      }
    }
  }, [branchesList]);

  const hasRowData = (row: SpreadsheetCell[]): boolean => {
    return row.some((cell) => cell?.value && cell.value.trim() !== "");
  };

  const handleDataChange = useCallback(
    (newData: SpreadsheetCell[][]) => {
      const nameColIndex = currentColumnConfig.findIndex(
        (c) => c.key === "Product Name",
      );
      const skuColIndex = currentColumnConfig.findIndex(
        (c) => c.key === "SKU Code",
      );

      const updatedData = newData.map((row) => {
        if (!hasRowData(row)) {
          return row.map((cell) => ({
            ...cell,
            isValid: true,
            error: undefined,
          }));
        }

        // Automatic SKU Code Generation
        if (nameColIndex >= 0 && skuColIndex >= 0) {
          const productName = row[nameColIndex]?.value?.trim() || "";
          const currentSku = row[skuColIndex]?.value?.trim() || "";
          if (productName && !currentSku) {
            row[skuColIndex].value = generateSkuCode(productName);
          }
        }

        return row.map((cell, colIndex) => {
          const cellValue = cell?.value?.trim() || "";
          const config = currentColumnConfig[colIndex];

          if (config.required && !cellValue) {
            return {
              ...cell,
              value: cellValue,
              isValid: false,
              error: `${config.key} is required`,
            };
          }

          if (config.validator) {
            const result = config.validator(cellValue, row);
            if (result === false)
              return {
                ...cell,
                value: cellValue,
                isValid: false,
                error: `Invalid ${config.key}`,
              };
            if (typeof result === "string")
              return {
                ...cell,
                value: cellValue,
                isValid: false,
                error: result,
              };
          }

          return { ...cell, value: cellValue, isValid: true, error: undefined };
        });
      });
      setData(updatedData);
    },
    [currentColumnConfig],
  );

  useEffect(() => {
    setData((prevData) => {
      if (prevData.length > 0) return prevData;
      return Array.from({ length: MIN_ROWS }, () =>
        currentColumnConfig.map((col) => ({
          value: "",
          type: col.type || "text",
        })),
      );
    });
  }, [currentColumnConfig]);

  const handleFileUpload = (file: File | null) => {
    if (!file) return;

    setIsLoading(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target?.result, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        }) as any[][];

        if (jsonData.length === 0) {
          toast.error("File is empty or invalid format");
          return;
        }

        // Identify Header Row and Column Mapping
        const headerRowIndex = jsonData.findIndex((row) =>
          currentColumnConfig.some((col) =>
            row.some(
              (cell: any) =>
                typeof cell === "string" &&
                cell.toLowerCase().trim() === col.key.toLowerCase().trim(),
            ),
          ),
        );

        const actualHeader =
          headerRowIndex >= 0 ? jsonData[headerRowIndex] : [];
        const colMapping = currentColumnConfig.map((config) => {
          const excelColIndex = actualHeader.findIndex(
            (h: any) =>
              typeof h === "string" &&
              h.toLowerCase().trim() === config.key.toLowerCase().trim(),
          );
          return excelColIndex;
        });

        const startRow = headerRowIndex >= 0 ? headerRowIndex + 1 : 0;
        let parsedData = jsonData.slice(startRow).map((row) =>
          currentColumnConfig.map((col, i) => {
            const excelIdx = colMapping[i];
            const val =
              excelIdx >= 0 &&
              row[excelIdx] !== undefined &&
              row[excelIdx] !== null
                ? String(row[excelIdx]).trim()
                : "";

            return {
              value: val,
              type: col.type || "text",
            };
          }),
        );

        if (parsedData.length > MAX_ROWS) {
          parsedData = parsedData.slice(0, MAX_ROWS);
          toast.warning(`File truncated to ${MAX_ROWS} rows`);
        }

        handleDataChange(parsedData);
        toast.success(`Imported ${parsedData.length} rows`);
      } catch (err) {
        toast.error("Failed to parse file");
        console.error(err);
      } finally {
        setIsLoading(false);
        resetRef.current?.();
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const downloadTemplate = () => {
    const header = currentColumnConfig.map((col) => col.key);
    const sampleRow = currentColumnConfig.map((col) => {
      if (col.key === "Product Name") return "Sample Product";
      if (col.key === "SKU Code") return "SKU-M-001";
      if (col.key === "Barcode") return "123456789";
      if (col.key === "Category") return categories[0]?.label || "Supplements";
      if (col.key === "Selling Price") return "100";
      if (col.key === "Purchase Price") return "80";
      if (col.key === "Unit") return "pcs";
      if (col.key === "Unit Value") return "1";
      if (col.key === "Status") return "Active";
      if (col.key === "HSN Code") return "8517";
      if (col.key === "Tax Type") return "Taxable";
      if (col.key === "Tax Slab") return "18";
      if (col.key === "Expiry Date") return "2026-12-31";
      if (col.key === "Manufacturing Date") return "2024-01-01";
      return "";
    });

    const templateData = [header, sampleRow];
    const sheet = XLSX.utils.aoa_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Template");
    XLSX.writeFile(workbook, `product_import_template.xlsx`);
  };

  const generateSkuCode = (name: string) => {
    if (!name) return "";
    const sanitized = name
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, "");
    const words = sanitized.split(/\s+/).filter((w) => w.length > 0);
    let prefix = "";
    if (words.length >= 2) {
      prefix = words.map((w) => w.substring(0, 2)).join("");
    } else if (words.length === 1) {
      prefix = words[0].substring(0, 4);
    } else {
      return "";
    }
    const random = Math.floor(1000 + Math.random() * 9000);
    return `${prefix}-SKU-${random}`.substring(0, 15);
  };

  const incrementPrefix = (prefixStartFromValue: string) => {
    if (!prefixStartFromValue) return "001";
    const numberValue = Number(prefixStartFromValue);
    const incrementedValue = numberValue + 1;
    const originalLeadingZeros =
      prefixStartFromValue.match(/^0*/)?.[0].length ?? 0;
    const totalLeadingZeros = Math.max(originalLeadingZeros, 3);
    return incrementedValue.toString().padStart(totalLeadingZeros, "0");
  };

  const handleSubmit = async () => {
    if (!companyId || data.length === 0) return;

    // Final Robust Validation Check
    const taxTypeColIndex = currentColumnConfig.findIndex(
      (c) => c.key === "Tax Type",
    );
    const taxSlabColIndex = currentColumnConfig.findIndex(
      (c) => c.key === "Tax Slab",
    );

    const problematicRows = data.filter((row) => {
      if (!hasRowData(row)) return false;

      // Dynamic check for Tax fields using column config indices
      const taxType =
        taxTypeColIndex >= 0 ? row[taxTypeColIndex]?.value?.trim() : undefined;
      const taxSlab =
        taxSlabColIndex >= 0 ? row[taxSlabColIndex]?.value?.trim() : undefined;
      if (!taxType) return true; // Tax Type is required
      if (taxType === "Taxable" && !taxSlab) return true; // Tax Slab required if Taxable

      return row.some((cell, colIndex) => {
        const config = currentColumnConfig[colIndex];
        const val = cell.value.trim();

        // Check Required
        if (config.required && !val) return true;

        // Check Validator
        if (config.validator) {
          const result = config.validator(val, row);
          if (result === false || typeof result === "string") return true;
        }

        return false;
      });
    });

    if (problematicRows.length > 0) {
      toast.error(
        `Cannot submit: ${problematicRows.length} rows have missing or invalid data (Tax Type and Tax Slab are mandatory). Please fix the highlighted errors.`,
      );
      return;
    }

    if (!branchId) {
      toast.warn("Please select a branch for import");
      return;
    }

    const validRows = data.filter((row) =>
      row.some((cell) => cell.value.trim() !== ""),
    );
    if (validRows.length === 0) {
      toast.warn("No data to submit");
      return;
    }

    setIsLoading(true);
    try {
      const batch = writeBatch(db);
      const userId = auth.currentUser?.uid;
      const userName = profileDetails
        ? `${profileDetails.first_name} ${profileDetails.last_name}`
        : "";

      // 1. Fetch Existing Products to avoid duplicates
      const existingProductsMap = new Map<string, any>(); // key: branchId_skuCode or branchId_name
      const productsSnap = await getDocs(
        query(collection(db, "companyprofile", companyId, "products")),
      );

      productsSnap.docs.forEach((doc) => {
        const p = doc.data();
        const branchIds = Array.isArray(p.branch_id)
          ? p.branch_id
          : [p.branch_id];

        branchIds.forEach((bId: string) => {
          if (p.sku)
            existingProductsMap.set(`${bId}_${p.sku}`, { id: doc.id, ...p });
          if (p.sku_code)
            existingProductsMap.set(`${bId}_${p.sku_code}`, {
              id: doc.id,
              ...p,
            });
          existingProductsMap.set(`${bId}_${p.name}`, { id: doc.id, ...p });
        });
      });

      const prefixDocRef = doc(
        db,
        "companyprofile",
        companyId,
        "companysettings",
        "customprefix",
      );
      const prefixSnap = await getDoc(prefixDocRef);
      let productPrefixData = prefixSnap.exists()
        ? prefixSnap.data().product
        : { prefix_increment_mode: "manual", prefix: "PRD", start_from: "001" };

      let currentVal = "";
      let mode = productPrefixData?.prefix_increment_mode || "manual";

      if (mode === "auto-generate") {
        currentVal = productPrefixData.start_from || "001";
      } else if (mode === "custom-generate") {
        currentVal = productPrefixData.custom_start_from || "001";
      }

      // Create or update documents
      for (const row of validRows) {
        const rowData: any = {};
        currentColumnConfig.forEach((col: any, i: any) => {
          let rawVal = row[i].value;
          let val: string | number = rawVal;
          if (col.type === "number") val = Number(rawVal);

          if (col.key === "Category") {
            rowData["category"] = val;
          } else if (col.key === "Preferred Vendor") {
            rowData["supplier"] = val;
          } else if (col.key === "Product Name") {
            rowData["name"] = typeof val === "string" ? val.trim() : val;
          } else if (col.key === "Selling Price") {
            rowData["price"] = val;
          } else if (col.key === "Purchase Price") {
            rowData["cost_price"] = val;
          } else if (col.key === "Stock Quantity") {
            rowData["stock_quantity"] = val;
          } else if (col.key === "Reorder Level") {
            rowData["reorder_level"] = val;
          } else if (col.key === "SKU Code") {
            rowData["sku_code"] = typeof val === "string" ? val.trim() : val;
          } else if (col.key === "Tax Type") {
            rowData["tax_type"] = val;
          } else if (col.key === "Tax Slab") {
            rowData["tax_slab"] = val;
          } else if (col.key === "Barcode") {
            rowData["barcode"] = val;
          } else if (col.key === "Unit") {
            rowData["unit"] = val;
          } else if (col.key === "Unit Value") {
            rowData["unit_value"] = val;
          } else if (col.key === "Description") {
            rowData["description"] = val;
          } else if (col.key === "HSN Code") {
            rowData["hsn_code"] = val;
          } else if (col.key === "Status") {
            rowData["status"] = val;
          } else if (col.key === "Expiry Date") {
            rowData["expiry_date"] = val;
          } else if (col.key === "Manufacturing Date") {
            rowData["manufacturing_date"] = val;
          }
        });

        // Generate SKU Code (human-friendly) if missing
        if (!rowData["sku_code"] && rowData["name"]) {
          rowData["sku_code"] = generateSkuCode(rowData["name"]);
        }

        const bId = branchId!;
        const bName = branchesList.find((b: any) => b.id === bId)?.name || bId;

        let finalProductCode = "";

        // Generate Product Code
        if (mode === "auto-generate") {
          finalProductCode = `${productPrefixData.prefix || "PRD"}-${currentVal}`;
          currentVal = incrementPrefix(currentVal);
        } else if (mode === "custom-generate") {
          finalProductCode = `${productPrefixData.custom_prefix || "PRD"}-${currentVal}`;
          currentVal = incrementPrefix(currentVal);
        } else {
          finalProductCode = `PRD-${Math.random().toString(36).substring(7).toUpperCase()}`;
        }

        const finalSku = rowData["sku_code"] || finalProductCode;

        // Handle Product Document (Master List with Branch Array)
        const globalExistingProd = Array.from(
          existingProductsMap.values(),
        ).find(
          (p) =>
            p.name === rowData.name ||
            (p.sku_code && p.sku_code === rowData.sku_code),
        );

        if (globalExistingProd) {
          const productDocRef = doc(
            db,
            "companyprofile",
            companyId,
            "products",
            globalExistingProd.id,
          );
          batch.update(productDocRef, {
            ...rowData,
            branch_id: Array.from(
              new Set([
                ...(Array.isArray(globalExistingProd.branch_id)
                  ? globalExistingProd.branch_id
                  : [globalExistingProd.branch_id]),
                bId,
              ]),
            ),
            company_id: companyId,
            is_inventory: false,
            last_modified_date: serverTimestamp(),
          });
        } else {
          const productDocRef = doc(
            collection(db, "companyprofile", companyId, "products"),
          );
          batch.set(productDocRef, {
            ...rowData,
            sku_code: rowData.sku_code || "",
            product_code: finalProductCode,
            is_inventory: false,
            company_id: companyId,
            branch_id: [bId],
            image_urls: [],
            manufacturing_date: rowData.manufacturing_date || null,
            expiry_date: rowData.expiry_date || null,
            created_by: userId,
            created_by_name: userName,
            created_at: serverTimestamp(),
            last_modified_date: serverTimestamp(),
            status: rowData.status || "Active",
          });
        }
      }

      // Update Prefix Index in DB
      if (mode === "auto-generate")
        batch.update(prefixDocRef, { "product.start_from": currentVal });
      else if (mode === "custom-generate")
        batch.update(prefixDocRef, { "product.custom_start_from": currentVal });

      await batch.commit();
      toast.success("Products imported successfully!");
      navigate("/products");
    } catch (error) {
      console.error("Error importing products:", error);
      toast.error("Failed to import products");
    } finally {
      setIsLoading(false);
    }
  };

  const renderValidationSummary = () => {
    if (validationStats.totalRows === 0) return null;

    const successRate = Math.round(
      (validationStats.validRows / validationStats.totalRows) * 100,
    );

    return (
      <Stack spacing="md" mt="lg">
        {/* Top Summary Bar */}
        <Paper
          p="sm"
          radius="md"
          bg="#FFF9DB"
          style={{ border: "1px solid #FFE066" }}
        >
          <Group position="apart">
            <Group spacing="xl">
              <Group spacing="xs">
                <IconFileText size={18} color="#E67700" />
                <Text size="sm" weight={700} color="#E67700">
                  Import Summary
                </Text>
              </Group>
              <Text size="xs" color="#E67700">
                <span style={{ fontWeight: 700 }}>
                  {validationStats.totalRows}
                </span>{" "}
                records processed
              </Text>
              <Text size="xs" color="#E67700">
                <span style={{ fontWeight: 700 }}>
                  {validationStats.invalidRows}
                </span>{" "}
                validation errors
              </Text>
              <Text size="xs" color="#E67700">
                <span style={{ fontWeight: 700 }}>{successRate}%</span> success
                rate
              </Text>
            </Group>
            <Group spacing="xs">
              <IconClock size={16} color="#E67700" />
              <Text size="xs" color="#E67700">
                Last updated just now
              </Text>
            </Group>
          </Group>
        </Paper>

        {/* Stats Cards */}
        <SimpleGrid
          cols={3}
          spacing="md"
          breakpoints={[{ maxWidth: "sm", cols: 1 }]}
        >
          {/* Valid Records Card */}
          <Paper p="md" radius="md" withBorder shadow="xs">
            <Group position="apart" align="flex-start">
              <div>
                <Text size="xs" color="dimmed" weight={500}>
                  Valid Records
                </Text>
                <Text size={rem(28)} weight={600} mt={4}>
                  {validationStats.validRows}
                </Text>
              </div>
              <ThemeIcon size="lg" radius="md" color="green" variant="light">
                <IconCheck size={20} />
              </ThemeIcon>
            </Group>
          </Paper>

          {/* Format Errors Card */}
          <Paper p="md" radius="md" withBorder shadow="xs">
            <Group position="apart" align="flex-start">
              <div>
                <Text size="xs" color="dimmed" weight={500}>
                  Validation Errors
                </Text>
                <Text size={rem(28)} weight={600} mt={4}>
                  {validationStats.errors.length}
                </Text>
              </div>
              <ThemeIcon size="lg" radius="md" color="red" variant="light">
                <IconFileText size={20} />
              </ThemeIcon>
            </Group>
          </Paper>

          {/* Invalid Rows Count */}
          <Paper p="md" radius="md" withBorder shadow="xs">
            <Group position="apart" align="flex-start">
              <div>
                <Text size="xs" color="dimmed" weight={500}>
                  Invalid Rows
                </Text>
                <Text size={rem(28)} weight={600} mt={4}>
                  {validationStats.invalidRows}
                </Text>
              </div>
              <ThemeIcon size="lg" radius="md" color="blue" variant="light">
                <IconPackage size={20} />
              </ThemeIcon>
            </Group>
          </Paper>
        </SimpleGrid>

        {/* Detailed Error List */}
        {validationStats.errors.length > 0 && (
          <Box>
            <Paper
              p="xs"
              radius="sm"
              withBorder
              bg="red.0"
              style={{ borderColor: "var(--mantine-color-red-2)" }}
            >
              <Group mb="xs" spacing="xs">
                <IconAlertCircle size={16} color="red" />
                <Text size="xs" weight={600} color="red">
                  Validation Details
                </Text>
              </Group>
              <ScrollArea style={{ height: 120 }}>
                {validationStats.errors.slice(0, 50).map((error, index) => (
                  <Text key={index} size="xs" color="red" mb={4}>
                    • Row {error.row}, {error.column}: {error.message}
                  </Text>
                ))}
                {validationStats.errors.length > 50 && (
                  <Text size="xs" color="dimmed" mt={4} pl={10}>
                    ... and {validationStats.errors.length - 50} more errors
                  </Text>
                )}
              </ScrollArea>
            </Paper>
          </Box>
        )}
      </Stack>
    );
  };

  return (
    <Container size="lg" mb="xl">
      {isLoading && <BenchLoader isLoading={isLoading} />}
      <Box
        sx={(theme) => ({
          position: "sticky",
          top: 110, // Avoid overlapping the fixed navbar
          zIndex: 100,
          backgroundColor: "#FAFAFA",
          paddingTop: theme.spacing.sm,
          //paddingBottom: theme.spacing.xs,
          marginTop: `-${theme.spacing.sm}`,
        })}
      >
        <SimpleGrid cols={2}>
          <BenchSettingsBreadcrumbs
            links={[
              { label: "Products", path: "/products" },
              { label: "Import" },
            ]}
          />
          <Group position="right">
            <Button
              onClick={handleSubmit}
              disabled={
                !branchId ||
                validationStats.invalidRows > 0 ||
                validationStats.totalRows === 0
              }
              leftIcon={<IconCheck size={16} />}
              loading={isLoading}
            >
              Submit Product Data
            </Button>
            <Group>
              <FileButton onChange={handleFileUpload} accept=".xlsx, .xls">
                {(props) => (
                  <Button
                    {...props}
                    leftIcon={<IconUpload size={16} />}
                    loading={isLoading}
                  >
                    Upload File
                  </Button>
                )}
              </FileButton>
            </Group>
            <Menu shadow="md">
              <Menu.Target>
                <Button
                  variant="outline"
                  rightIcon={<IconChevronDown size={16} />}
                >
                  More Actions
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  icon={<IconDownload size={16} />}
                  onClick={downloadTemplate}
                >
                  Download Template
                </Menu.Item>
                {/* <Menu.Item icon={<IconArrowLeft size={16} />} onClick={() => navigate("/products")}>
                                Back to Products
                            </Menu.Item> */}
              </Menu.Dropdown>
            </Menu>
          </Group>
        </SimpleGrid>

        <Divider my="sm" />
      </Box>

      <BenchTopHeader
        icon={<IconPackage />}
        title={"Product Import"}
        description={"Import product records directly to specific branches"}
      />

      <Group position="right" mb="md" align="flex-end">
        <Select
          withinPortal
          label={
            <Group spacing={4} noWrap>
              <span>Select Branch</span>
              <span style={{ color: "red" }}>*</span>
              <Tooltip label="Choose the branch where imported products will be assigned">
                <IconInfoCircle size={16} style={{ cursor: "pointer" }} />
              </Tooltip>
            </Group>
          }
          placeholder="Select branch"
          data={
            branchesList
              ?.filter((b: any) => b.id !== "all_branches")
              .map((b: any) => ({ value: b.id, label: b.name })) || []
          }
          value={branchId}
          onChange={(val) => setBranchId(val || "")}
          style={{ width: 300 }}
        />
      </Group>

      <Stack spacing="lg">
        {/* Validation Summary */}
        {renderValidationSummary()}

        {/* Reusable Spreadsheet */}
        {/* <Paper radius="lg" withBorder shadow="sm" style={{ height: '500px', display: 'flex', flexDirection: 'column' }}> */}
        <ReusableSpreadsheet
          data={data}
          columns={currentColumnConfig}
          onChange={handleDataChange}
          onValidationChange={setValidationStats}
          minRows={MIN_ROWS}
          maxRows={MAX_ROWS}
          showValidation={true}
          highlightErrors={true}
        />
        {/* </Paper> */}
      </Stack>
    </Container>
  );
}
